import { Box, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Sparkline from '@/components/researcher/Sparkline'

export interface KpiItem {
  label: string
  value: string
  suffix: string
  delta?: string
  deltaKind?: 'pos' | 'neg' | 'neutral'
  spark?: number[]
}

/** Six-up KPI strip with optional delta + inline sparkline per card. */
export default function KpiStrip({ items }: { items: KpiItem[] }) {
  const theme = useTheme()
  const c = theme.affexy.colors

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' }, gap: 1.5 }}>
      {items.map((k) => {
        const deltaColor = k.deltaKind === 'pos' ? c.success : k.deltaKind === 'neg' ? c.danger : c.ink56
        return (
          <Paper
            key={k.label}
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: '14px',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: c.ink48,
                fontWeight: 700,
              }}
            >
              {k.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 26,
                  color: c.ink,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.01em',
                  lineHeight: 1,
                }}
              >
                {k.value}
              </Typography>
              <Typography sx={{ fontSize: 12, color: c.ink56 }}>{k.suffix}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 18 }}>
              {k.delta && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: 11,
                    color: deltaColor,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {k.deltaKind === 'pos' ? (
                    <ArrowDropUpIcon sx={{ fontSize: 16, mr: -0.25 }} />
                  ) : k.deltaKind === 'neg' ? (
                    <ArrowDropDownIcon sx={{ fontSize: 16, mr: -0.25 }} />
                  ) : (
                    <Box component="span" sx={{ mr: 0.5 }}>
                      ·
                    </Box>
                  )}
                  {k.delta}
                </Box>
              )}
              {k.spark && <Sparkline points={k.spark} height={18} color={k.deltaKind === 'neg' ? c.danger : c.coral} />}
            </Box>
          </Paper>
        )
      })}
    </Box>
  )
}
